import { ConfigProvider, Select } from "antd";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { mixGetV2 } from "services/mixin";
import "../../components/Account/styles.scss";

interface AcademyCredentialsModalProps {
    onClose: () => void;
    onSave?: (value: { accountId: string, nickName: string, balanceSize: number, platform: string, broker: string } | null) => void;
}

const AcademyCredentialsModal: React.FC<AcademyCredentialsModalProps> = ({ onClose, onSave }) => {
    const [nickname, setNickname] = useState('');
    const [accountId, setAccountId] = useState('');
    const [balanceSize, setBalanceSize] = useState('');
    const [platform, setPlatform] = useState('');
    const [broker, setBroker] = useState('');
    const { t } = useTranslation();
    const [eror, setError] = useState([
        { field: 'accountId', message: '', error: false },
        { field: 'nickname', message: '', error: false },
        { field: 'balanceSize', message: '', error: false },
        { field: 'platform', message: '', error: false },
        { field: 'broker', message: '', error: false }
    ]);

    const [brokerList, setBrokerList] = useState<{ value: string, label: string }[]>([]);

    async function checkData() {
        const errors = [];
        if (!accountId) {
            errors.push({ field: 'accountId', message: 'Account Number is required', error: true });
        }
        else {
            errors.push({ field: 'accountId', message: '', error: false });
        }
        if (!nickname) {
            errors.push({ field: 'nickname', message: 'Nickname is required', error: true });
        }
        else {
            errors.push({ field: 'nickname', message: '', error: false });
        }
        if (!platform) {
            errors.push({ field: 'platform', message: 'Platform is required', error: true });
        }
        else {
            errors.push({ field: 'platform', message: '', error: false });
        }
        if (!broker) {
            errors.push({ field: 'broker', message: 'Broker is required', error: true });
        }
        else {
            errors.push({ field: 'broker', message: '', error: false });
        }
        setError(errors);
        if (errors.some(e => e.error)) {
            return false;
        }
        return true;
    }

    async function fetchBrokers() {
        try {
            const response = await mixGetV2('/terminal-accounts/brokers');
            const brokers = (response.data || []).map((value: string) => ({
                value: value,
                label: value
            }));

            setBrokerList(brokers);
        } catch (error) {
            console.error('Error fetching brokers:', error);
        }
    }

    useEffect(() => {
        fetchBrokers();
    }, []);

    return (
        <>
            <div
                className="fixed inset-0 bg-black bg-opacity-50 z-[999]"
                onClick={onClose}
                style={{ pointerEvents: 'auto' }}
            ></div>

            {/* Modal */}
            <div
                id="radix-modal"
                aria-describedby="radix-modal-desc"
                aria-labelledby="radix-modal-title"
                data-state="open"
                className="flex cursor-default flex-col rounded-[4px] bg-background-secondary data-[state=open]:animate-contentShow center-x center-y fixed max-h-[85vh] w-[100vw] max-w-[352px] focus:outline-none sm:w-[90vw] sm:max-w-[768px] _content_aboc3_3"
                style={{ pointerEvents: 'auto' }}
            >
                {/* Header */}
                <div className="flex items-baseline justify-between px-4 pt-3">
                    <h2 id="radix-modal-title" className="m-0 break-words text-headings-h5 text-content-primary">{t('NewAccount')}</h2>
                    <div className="flex-grow"></div>
                    <div className="_container_4x09j_3 group-hover:bg-background-hover group-active:bg-background-primary">
                        <button type="button" className="_button_4x09j_27 _size-large_4x09j_57 _kind-without-border_4x09j_75 text-content-tertiary" onClick={onClose}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2.72477 1L1.05505 2.66972L10.3578 11.9725L1 21.3486L2.65138 23L12.0275 13.6422L21.3853 23L23.055 21.3303L13.6972 11.9725L23 2.66972L21.3303 1L12.0275 10.3028L2.72477 1Z" fill="currentColor"></path>
                            </svg>
                        </button>
                    </div>
                </div>
                <div className="h-px bg-separators-primary mt-3"></div>

                {/* Content */}
                <div className="px-4 sm:px-[24px] md:px-[32px] lg:px-[40px] flex-basis-0 flex h-min min-h-0 flex-shrink flex-grow flex-col overflow-auto pb-4 pt-3">
                    <div className="grid gap-2">
                        {/* Account Number */}
                        <div className="flex flex-col gap-[8px]">
                            <div className="_sectionLabelContainer_8d9j0_14">
                                <span className="_sectionLabel_8d9j0_7">{t('AccountNumber')}</span>
                            </div>
                            <div className="group transition-all duration-150 rounded focus-within:bg-transparent group relative flex w-full border border-solid border-border-secondary bg-background-tertiary focus-within:border-functional-active">
                                <input
                                    type="number"
                                    name="accountId"
                                    value={accountId}
                                    onChange={(e) => setAccountId(e.target.value)}
                                    className="_small_fzuuo_13 max-h-[300px] w-full rounded border-none bg-transparent text-labels-medium text-content-primary caret-content-active placeholder:text-content-primary focus:text-content-secondary !overflow-x-hidden"
                                />
                            </div>
                            {eror.find(e => e.field === 'accountId')?.error && (
                                <div className="text-red-500 text-sm">
                                    {eror.find(e => e.field === 'accountId')?.message}
                                </div>
                            )}
                        </div>

                        {/* Nickname */}
                        <div className="flex flex-col gap-[8px]">
                            <div className="_sectionLabelContainer_8d9j0_14">
                                <span className="_sectionLabel_8d9j0_7">{t('NickName')}</span>
                            </div>
                            <div className="group transition-all duration-150 rounded focus-within:bg-transparent group relative flex w-full border border-solid border-border-secondary bg-background-tertiary focus-within:border-functional-active">
                                <input
                                    type="text"
                                    name="nickName"
                                    value={nickname}
                                    onChange={(e) => setNickname(e.target.value)}
                                    className="_small_fzuuo_13 max-h-[300px] w-full rounded border-none bg-transparent text-labels-medium text-content-primary caret-content-active placeholder:text-content-primary focus:text-content-secondary !overflow-x-hidden"
                                />
                            </div>
                            {eror.find(e => e.field === 'nickname')?.error && (
                                <div className="text-red-500 text-sm">
                                    {eror.find(e => e.field === 'nickname')?.message}
                                </div>
                            )}
                        </div>

                        {/* Platform */}
                        <div className="flex flex-col gap-[8px]">
                            <div className="_sectionLabelContainer_8d9j0_14">
                                <span className="_sectionLabel_8d9j0_7">{t('Platform')}</span>
                            </div>
                            <ConfigProvider
                                theme={{
                                    components: {
                                        Select: {
                                            zIndexPopup: 2500,
                                        },
                                    },
                                }}
                            >
                                <Select
                                    size="large"
                                    className="custom-select w-full"
                                    placeholder="Select a platform"
                                    onChange={(value) => setPlatform(value)}
                                    options={[
                                        { value: 'mt4', label: 'MT4' },
                                        { value: 'mt5', label: 'MT5' },
                                        { value: 'ctrader', label: 'cTrader' }
                                    ]}
                                    value={platform}
                                />
                                {eror.find(e => e.field === 'platform')?.error && (
                                    <div className="text-red-500 text-sm mt-1">
                                        {eror.find(e => e.field === 'platform')?.message}
                                    </div>
                                )}
                            </ConfigProvider>
                        </div>

                        {/* Broker */}
                        <div className="flex flex-col gap-[8px]">
                            <div className="_sectionLabelContainer_8d9j0_14">
                                <span className="_sectionLabel_8d9j0_7">Broker</span>
                            </div>
                            <ConfigProvider
                                theme={{
                                    components: {
                                        Select: {
                                            zIndexPopup: 2500,
                                        },
                                    },
                                }}
                            >
                                <Select
                                    size="large"
                                    className="custom-select w-full"
                                    placeholder="Select a broker"
                                    onChange={(value) => setBroker(value)}
                                    options={brokerList}
                                    value={broker}
                                />
                                {eror.find(e => e.field === 'broker')?.error && (
                                    <div className="text-red-500 text-sm mt-1">
                                        {eror.find(e => e.field === 'broker')?.message}
                                    </div>
                                )}
                            </ConfigProvider>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="h-px bg-separators-primary"></div>
                <div className="px-4 sm:px-[24px] md:px-[32px] lg:px-[40px] flex flex-col items-stretch justify-end gap-2 pb-4 pt-3 sm:flex-row">
                    <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:justify-end">
                        <button
                            type="button"
                            className="_button_rcmwj_3 _size-large_rcmwj_242 _type-secondary_rcmwj_71 _kind-filled_rcmwj_31 w-full md:w-auto"
                            onClick={onClose}
                        >
                            {t('Back')}
                        </button>
                        <button
                            type="submit"
                            className="_button_rcmwj_3 _size-large_rcmwj_242 _type-primary_rcmwj_31 _kind-filled_rcmwj_31 w-full md:w-auto"
                            onClick={() => {
                                checkData().then((isValid) => {
                                    if (isValid && onSave) {
                                        onSave({
                                            accountId: accountId,
                                            nickName: nickname,
                                            balanceSize: Number(balanceSize.replace(/,/g, '')),
                                            platform: platform,
                                            broker: broker
                                        });
                                    }
                                });
                            }}
                        >
                            {t('Save')}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AcademyCredentialsModal;
